import { supabase } from './client'

export type NotificationData =
  | { appointment_id?: string; appointmentId?: string; sender_role?: 'patient' | 'provider' }
  | Record<string, unknown>

export interface Notification {
  id: string
  patient_id?: string
  provider_id?: string
  type: 'appointment' | 'payment' | 'reminder' | 'system' | 'promotion' | 'withdrawal' | 'earning'
  title: string
  message: string
  data?: NotificationData
  read: boolean
  created_at: string
  updated_at: string
}

function mapNotificationRow(row: any): Notification {
  return {
    id: row.id,
    patient_id: row.patient_id || undefined,
    provider_id: row.provider_id || undefined,
    type: row.type,
    title: row.title,
    message: row.message,
    data: row.data && typeof row.data === 'object' ? (row.data as NotificationData) : undefined,
    read: row.read ?? false,
    created_at: row.created_at,
    updated_at: row.updated_at,
  }
}

function getAppointmentIdFromData(data?: NotificationData): string | null {
  if (!data || typeof data !== 'object') return null
  const candidate = (data as Record<string, unknown>).appointment_id ?? (data as Record<string, unknown>).appointmentId
  return typeof candidate === 'string' && candidate.trim().length > 0 ? candidate : null
}

export const notificationService = {
  async getPatientNotifications(patientId: string): Promise<Notification[]> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data || []).map(mapNotificationRow)
    } catch {
      return []
    }
  },

  async getUnreadCountForPatient(patientId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('patient_id', patientId)
        .eq('read', false)

      if (error) throw error
      return count || 0
    } catch {
      return 0
    }
  },

  async getProviderNotifications(providerId: string): Promise<Notification[]> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('provider_id', providerId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data || []).map(mapNotificationRow)
    } catch {
      return []
    }
  },

  async getUnreadCountForProvider(providerId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('provider_id', providerId)
        .eq('read', false)

      if (error) throw error
      return count || 0
    } catch {
      return 0
    }
  },

  async markAsRead(notificationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)

      if (error) throw error
      return { success: true }
    } catch (error: any) {
      return { success: false, error: error.message }
    }
  },

  async markAllAsReadForPatient(patientId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('patient_id', patientId)
        .eq('read', false)

      if (error) throw error
      return { success: true }
    } catch (error: any) {
      return { success: false, error: error.message }
    }
  },

  async markAllAsReadForProvider(providerId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('provider_id', providerId)
        .eq('read', false)

      if (error) throw error
      return { success: true }
    } catch (error: any) {
      return { success: false, error: error.message }
    }
  },

  subscribeToPatientNotifications(patientId: string, callback: (notification: Notification) => void) {
    return supabase
      .channel(`patient-notifications-${patientId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `patient_id=eq.${patientId}`,
        },
        (payload) => callback(mapNotificationRow(payload.new as any))
      )
      .subscribe()
  },

  subscribeToProviderNotifications(providerId: string, callback: (notification: Notification) => void) {
    return supabase
      .channel(`provider-notifications-${providerId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `provider_id=eq.${providerId}`,
        },
        (payload) => callback(mapNotificationRow(payload.new as any))
      )
      .subscribe()
  },

  async resolvePatientNotificationRoute(notification: Pick<Notification, 'data'>): Promise<string | null> {
    const appointmentId = getAppointmentIdFromData(notification.data)
    if (!appointmentId) return null

    const { data, error } = await supabase
      .from('appointments')
      .select('service_type')
      .eq('id', appointmentId)
      .maybeSingle()

    if (error) return null
    return data?.service_type === 'Chat Consultation'
      ? `/patient/chat/${appointmentId}`
      : `/patient/appointments/${appointmentId}`
  },

  async resolveProviderNotificationRoute(notification: Pick<Notification, 'data'>): Promise<string | null> {
    const appointmentId = getAppointmentIdFromData(notification.data)
    if (!appointmentId) return null

    const { data, error } = await supabase
      .from('appointments')
      .select('service_type')
      .eq('id', appointmentId)
      .maybeSingle()

    if (error) return null
    return data?.service_type === 'Chat Consultation'
      ? `/provider/chat/${appointmentId}`
      : `/provider/appointments/${appointmentId}`
  },
}